import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout, Spin } from "antd";
import { Container } from "react-bootstrap";
import HeaderIndex from "../Auth/Header";
import FooterIndex from "../Auth/Footer";

const { Content } = Layout;

const getApiBaseUrl = () => {
  const configured =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    "https://backend.imergey.com/api";

  return configured.replace(/\/+$/, "");
};

const sanitizeHtml = (html = "") =>
  html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "");

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

export default function PublicBlogArticle() {
  const { id, blogSlug } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const apiUrl = useMemo(() => {
    const base = getApiBaseUrl();
    if (id) return `${base}/user/blog/public-data/${id}`;
    return `${base}/user/blog/public-data/slug/${blogSlug}`;
  }, [id, blogSlug]);

  useEffect(() => {
    let isMounted = true;

    const loadArticle = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await fetch(apiUrl);
        const result = await response.json();

        if (!response.ok || !result?.data) {
          throw new Error(result?.message || "Article not found");
        }

        if (isMounted) setArticle(result.data);
      } catch (err) {
        if (isMounted) setError(err.message || "Article not found");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadArticle();

    return () => {
      isMounted = false;
    };
  }, [apiUrl]);

  useEffect(() => {
    if (!article) return;

    document.title = `${article.topic || "Blog"} | Imergey`;
    const description =
      stripHtml(article.content || "").slice(0, 155) ||
      "Read the full article on Imergey.";

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, [article]);

  return (
    <Layout className="imergey-layout">
      <HeaderIndex />
      <Content>
        <section className="public-blog-page">
          <Container>
            {isLoading ? (
              <div className="public-blog-state">
                <Spin size="large" />
              </div>
            ) : error ? (
              <div className="public-blog-state">
                <h1>Article not found</h1>
              </div>
            ) : (
              <article className="public-blog-article">
                <div className="public-blog-meta">
                  {article?.author?.name || "Imergey"} / Blog
                </div>
                <h1>{article.topic || "Untitled blog"}</h1>
                <div
                  className="public-blog-content"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(article.content || ""),
                  }}
                />
              </article>
            )}
          </Container>
        </section>
      </Content>
      <FooterIndex />
    </Layout>
  );
}
